import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';

export type Contact = {
  id: string;
  name: string;
  photo?: string;
  lastMessage?: string;
  lastMessageTime?: string;
  unread?: number;
  conversationId?: string;
};

export function useConversation() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const createOrGetConversation = async (contact: { id: string; name: string; photo?: string }) => {
    if (!user) return null;
    
    setIsLoading(true);
    try {
      // Check if conversation already exists
      const { data: userParticipations } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user.id);

      if (userParticipations?.length) {
        const { data: contactParticipations } = await supabase
          .from('conversation_participants')
          .select('conversation_id')
          .eq('user_id', contact.id)
          .in('conversation_id', userParticipations.map(p => p.conversation_id));

        if (contactParticipations?.length) {
          // Conversation exists
          return {
            conversationId: contactParticipations[0].conversation_id,
            contact: {
              ...contact,
              conversationId: contactParticipations[0].conversation_id,
              lastMessage: 'Loading conversation...',
              lastMessageTime: 'Now',
              unread: 0
            }
          };
        }
      }

      // Create new conversation
      const { data: newConv, error: newConvError } = await supabase
        .from('conversations')
        .insert({})
        .select()
        .single();

      if (newConvError) throw newConvError;

      await supabase.from('conversation_participants').insert([
        { conversation_id: newConv.id, user_id: user.id },
        { conversation_id: newConv.id, user_id: contact.id }
      ]);

      return {
        conversationId: newConv.id,
        contact: {
          ...contact,
          conversationId: newConv.id,
          lastMessage: 'New conversation',
          lastMessageTime: 'Just now',
          unread: 0
        }
      };
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create conversation."
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createOrGetConversation,
    isLoading
  };
} 